import type { CollaborationRuntimeAccess } from './collaborationThreadShared';

export type RecipientPolicy = 'registered_contacts_only' | 'verified_domain_any' | 'named_delegates';
export type CollaborationPartnerStatus =
  | 'draft'
  | 'awaiting_supplier_registration'
  | 'awaiting_activation'
  | 'declined'
  | 'active'
  | 'suspended'
  | 'deactivated'
  | 'archived';
export type CollaborationPartnerFootprintMode = 'portal_only' | 'collaboration_instance';
export type CollaborationPartnerRiskTier = 'low' | 'medium' | 'high';
export const collaborationPartnerRiskTierOptions: ReadonlyArray<{ value: CollaborationPartnerRiskTier; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' }
];
export type CollaborationContactType = 'person' | 'shared_mailbox';
export type CollaborationTenantInstanceMode = 'standard_external' | 'rich_internal';
export type CollaborationInstanceDirectionFlag = 'outbound' | 'inbound';
/**
 * Share envelope discriminator (tracker #52 slice 1): 'finding' is the
 * existing supplier-collaboration share; 'self_assessment' sends a sealed
 * audit-template questionnaire the supplier completes themselves.
 */
export type CollaborationShareKind = 'finding' | 'self_assessment';
export type CollaborationInboundCustomerRelationshipStatus = 'pending_acceptance' | 'accepted' | 'blocked';
export type CollaborationPartnerActivationErrorCode =
  | 'COLLABORATION_PARTNER_NO_PRINCIPAL_CONTACT'
  | 'COLLABORATION_PARTNER_NO_SUPPLIER_ORGANISATION'
  | 'COLLABORATION_PARTNER_INVALID_STATUS_FOR_ACTIVATION';

export interface CollaborationPartnerActivationResult {
  status: 'success' | 'error';
  message: string;
  errorCode?: CollaborationPartnerActivationErrorCode | string;
}

export interface CollaborationPolicy {
  policyId: string;
  tenantId: string;
  recipientPolicy: RecipientPolicy;
  allowUnregisteredView: boolean;
  requireVerifiedEmail: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

export interface CollaborationPartner {
  partnerId: string;
  displayName: string;
  primaryDomain: string | null;
  externalSupplierReference: string | null;
  defaultReplyToEmail: string | null;
  status: CollaborationPartnerStatus;
  footprintMode: CollaborationPartnerFootprintMode;
  supplierCategory?: string | null;
  riskTier?: CollaborationPartnerRiskTier | null;
  updatedAt: string;
}

export interface CollaborationPartnerTemplateReplyTo {
  partnerTemplateReplyToId: string;
  partnerId: string;
  templateKey: string;
  replyToEmail: string;
  updatedAt: string;
  updatedBy: string | null;
}

export interface CollaborationPartnerTemplateAuthorisation {
  partnerTemplateAuthorisationId: string;
  partnerId: string;
  contactId: string | null;
  templateKey: string;
  updatedAt: string;
  updatedBy: string | null;
}

export interface CollaborationContact {
  contactId: string;
  partnerId: string | null;
  emailNormalized: string;
  displayName: string | null;
  contactType: CollaborationContactType;
  isPrincipalContact: boolean;
  status: 'active' | 'disabled';
  updatedAt: string;
}

export interface CollaborationInstanceParentBinding {
  parentBindingId: string;
  parentTenantId: string;
  partnerId: string | null;
  isolationBoundaryKind: 'instance_singleton' | 'per_customer_relationship';
  isolationBoundaryKey: string;
  status: 'active' | 'revoked';
  createdAtUtc: string;
  revokedAtUtc: string | null;
}

export interface CollaborationInstance {
  instanceId: string;
  siteUrl: string;
  displayName: string | null;
  acceptsCollaboration: boolean;
  collaborationOutbound: boolean;
  collaborationInbound: boolean;
  collaborationMode?: CollaborationTenantInstanceMode;
  defaultCollaborationReplyToEmail: string | null;
  isActive: boolean;
  parentBindings: CollaborationInstanceParentBinding[];
}

export interface CollaborationInboxItem {
  inboxItemId: string;
  status: string;
  instanceId: string | null;
  parentTenantId: string | null;
  partnerId: string | null;
  inboundOriginShareInvitationId: string | null;
  localBaselineVersion: string | null;
  latestSourceBaselineVersion: string | null;
  updateAvailable: boolean;
  pendingBaselineAcknowledgement: boolean;
  baselinePulledAtUtc: string | null;
  baselinePulledBy: string | null;
  baselineAcknowledgedAtUtc: string | null;
  baselineAcknowledgedBy: string | null;
  updatedAt: string;
  runtimeAccess: CollaborationRuntimeAccess;
  invitation: {
    tenantId: string;
    createdBy: string | null;
    partnerId: string | null;
    parentTenantId: string | null;
    inboundOriginShareInvitationId: string | null;
    kind: CollaborationShareKind;
    selfAssessmentFirstOpenedAtUtc?: string | null;
    selfAssessmentSubmittedAtUtc?: string | null;
    recipientEmailNormalized: string;
    recipientTenantId: string | null;
    status: string;
    expiresAt: string;
    payloadJson: string;
    createdAt: string;
  };
  inboundCustomerRelationship: CollaborationInboundCustomerRelationship;
}

export interface CollaborationInboundCustomerRelationship {
  inboundCustomerRelationshipId: string;
  supplierTenantId: string;
  sourceCustomerTenantId: string;
  partnerId: string | null;
  sourceCustomerDisplayName: string;
  status: CollaborationInboundCustomerRelationshipStatus;
  sharePointGroupId: string | null;
  sharePointGroupName: string | null;
  firstSeenAtUtc: string;
  lastActivityAtUtc: string;
  createdAtUtc: string;
  createdBy: string | null;
  updatedAtUtc: string;
  updatedBy: string | null;
  rowVersion: string;
}

export interface CollaborationShareInvitation {
  shareInvitationId: string;
  createdBy: string | null;
  partnerId: string | null;
  parentTenantId: string | null;
  inboundOriginShareInvitationId: string | null;
  kind: CollaborationShareKind;
  /** Self-assessment lifecycle stamps (tracker #52 slice 4): sent = created, opened = first supplier open, submitted = envelope landed. */
  selfAssessmentFirstOpenedAtUtc?: string | null;
  selfAssessmentSubmittedAtUtc?: string | null;
  recipientContactId: string | null;
  recipientEmailNormalized: string;
  recipientTenantId: string | null;
  status: 'pending' | 'accepted' | 'rejected' | 'imported' | 'revoked' | 'expired';
  expiresAt: string;
  resolvedReplyToEmail: string | null;
  payloadJson: string;
  createdAt: string;
  updatedAt: string;
}

export interface CollaborationPartnerAdminInviteResult {
  partnerId: string;
  partnerName: string;
  invitedEmail: string;
  expiresAt: string;
  inviteUrl: string;
}

/** Supplier Profile page (tracker #7) — partner detail as the profile route returns it (list-view shape plus linkage/audit fields). */
export interface CollaborationPartnerProfilePartner extends CollaborationPartner {
  supplierOrganisationId: string | null;
  createdAt: string;
  updatedBy: string | null;
}

/** Admin-invite history entry (token hash deliberately absent from the wire). */
export interface CollaborationPartnerAdminInviteSummary {
  adminInviteId: string;
  contactId: string;
  invitedEmailNormalized: string;
  status: 'pending' | 'redeemed' | 'revoked' | 'expired' | string;
  expiresAt: string;
  redeemedAt: string | null;
  createdBy: string | null;
  createdAt: string;
}

/** Linked supplier organisation summary — presence means the supplier admin has Microsoft-registered. */
export interface CollaborationSupplierOrganisationSummary {
  supplierOrganisationId: string;
  displayName: string;
  primaryDomain: string | null;
  status: string;
  adminCount: number;
  lastAdminSignInAt: string | null;
  termsAcceptance: {
    termsVersionId: string;
    acceptedAt: string;
  } | null;
}

export type CollaborationPartnerRecentShare = Pick<
  CollaborationShareInvitation,
  'shareInvitationId' | 'kind' | 'recipientEmailNormalized' | 'status' | 'createdAt' | 'updatedAt'
>;

export interface CollaborationPartnerProfile {
  partner: CollaborationPartnerProfilePartner;
  adminInvites: CollaborationPartnerAdminInviteSummary[];
  supplierOrganisation: CollaborationSupplierOrganisationSummary | null;
  contacts: CollaborationContact[];
  templateAuthorisations: CollaborationPartnerTemplateAuthorisation[];
  templateReplyTos: CollaborationPartnerTemplateReplyTo[];
  recentShares: CollaborationPartnerRecentShare[];
  hasInstanceDefaultReplyTo: boolean;
}

export interface CollaborationPrincipalContactChangeRequest {
  principalContactChangeRequestId: string;
  partnerId: string;
  partnerDisplayName: string | null;
  currentContactId: string;
  currentContactEmailNormalized: string;
  currentContactDisplayName: string | null;
  proposedContactId: string;
  proposedContactEmailNormalized: string;
  proposedContactDisplayName: string | null;
  status: 'pending' | 'approved' | 'rejected';
  requestedBy: string | null;
  decisionReason: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CollaborationPolicyFormState {
  recipientPolicy: RecipientPolicy;
  allowUnregisteredView: boolean;
  requireVerifiedEmail: boolean;
}

export interface CollaborationPartnerFormState {
  displayName: string;
  primaryDomain: string;
  externalSupplierReference: string;
  defaultReplyToEmail: string;
  footprintMode: CollaborationPartnerFootprintMode;
  supplierCategory: string;
  riskTier: CollaborationPartnerRiskTier | '';
}

export interface SupplierImportRowSnapshot {
  importRowId: string;
  rowNumber: number;
  raw: Record<string, string>;
  normalized: {
    supplierName: string;
    externalSupplierReference: string;
    primaryDomain: string;
    contactName: string;
    contactEmail: string;
    defaultReplyToEmail: string;
    supplierCategory?: string;
    riskTier?: string;
    identityKey: string;
    identityKind: 'external_reference' | 'display_name';
  };
  validationStatus: 'valid' | 'warning' | 'invalid' | 'duplicate_in_file';
  validationErrors: string[];
  matchedPartnerId: string | null;
  selectedForCommit: boolean;
  commitStatus: 'created' | 'updated' | 'skipped' | 'failed' | null;
  committedPartnerId: string | null;
  committedContactId: string | null;
}

export interface SupplierImportBatchSnapshot {
  importBatchId: string;
  tenantId: string;
  originalFileName: string;
  fileSizeBytes: number;
  status: 'previewed' | 'committed' | 'discarded' | 'failed';
  summary: Record<string, unknown>;
  createdAtUtc: string;
  committedAtUtc: string | null;
  rows: SupplierImportRowSnapshot[];
}

export interface CollaborationPartnerTemplateReplyToFormState {
  partnerId: string;
  templateKey: string;
  replyToEmail: string;
}

export interface CollaborationPartnerTemplateAuthorisationFormState {
  partnerId: string;
  contactId: string;
  templateKey: string;
}

export interface CollaborationContactFormState {
  partnerId: string;
  email: string;
  displayName: string;
  contactType: CollaborationContactType;
  isPrincipalContact: boolean;
}

export interface CollaborationAssignmentFormState {
  partnerId: string;
  contactId: string;
  instanceId: string;
}

export const policyLabel: Record<RecipientPolicy, string> = {
  registered_contacts_only: 'Registered Contacts Only',
  verified_domain_any: 'Verified Domain Any',
  named_delegates: 'Named Delegates'
};

export const collaborationModeLabel: Record<CollaborationTenantInstanceMode, string> = {
  standard_external: 'Standard external',
  rich_internal: 'Rich internal'
};

export const collaborationDirectionLabel: Record<CollaborationInstanceDirectionFlag, string> = {
  outbound: 'Outbound',
  inbound: 'Inbound'
};
